import type { AnchorHTMLAttributes, MouseEvent } from 'react';
import { cn } from '@/utils/cn';
import { hasNativeJSBridge } from '@/utils/bridge-runtime';

export const Link = ({
  href,
  onClick,
  className,
  children,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) => {
  if (isBlockedHTMLPreviewDataURL(href)) {
    return (
      <span
        className={cn('text-text-secondary cursor-not-allowed', className)}
        title="HTML previews must be published before they can be opened."
      >
        {children}
      </span>
    );
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);

    if (
      !href ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    if (!hasNativeJSBridge()) {
      return;
    }

    event.preventDefault();
    const openInPreview =
      window.jsb?.MessagesBridge?.previewURL ??
      window.jsb?.UtilsBridge?.openURL;
    if (!openInPreview) {
      return;
    }

    openInPreview(href).catch(error => {
      console.error(error);
      window.open(href, '_blank', 'noopener,noreferrer');
    });
  };

  return (
    <a
      onClick={handleClick}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'text-apple-blue hover:underline cursor-pointer',
        className
      )}
      {...props}
    >
      {children}
    </a>
  );
};

export function isBlockedHTMLPreviewDataURL(href: string | undefined) {
  return href?.trim().toLowerCase().startsWith('data:text/html') ?? false;
}
